// ============================================================================
// JARVISTRAVEL - FATIGUE INDEX primitives
// Reusable, on-brand FI visuals shared across Home / Trip Plan / Journal / the
// FI modal / scenario compare. All read from the shared fi.ts scale so the
// colors + language stay consistent (chill blue → balanced green → packed red).
// ============================================================================

import { memo } from 'react';
import { fiBand, fiBandLabel, fiDescriptor, fiToneForScore, clampFi } from '@/app/utils/fi';

type Size = 'sm' | 'md' | 'lg';

const SIZE: Record<Size, string> = {
  sm: 'gap-1 px-2 py-0.5 text-xs',
  md: 'gap-1.5 px-2.5 py-1 text-sm',
  lg: 'gap-2 px-3 py-1.5 text-base',
};

/** Compact FI badge: the 1–9 numeral + band label (+ optional descriptor). */
export const FIBadge = memo(function FIBadge({ score, size = 'md', showLabel = true, showDescriptor = false, className = '' }: {
  score: number; size?: Size; showLabel?: boolean; showDescriptor?: boolean; className?: string;
}) {
  const band = fiBand(score);
  const tone = fiToneForScore(score);
  return (
    <span className={`inline-flex items-center rounded-md font-medium ${tone.bg} ${tone.text} ${SIZE[size]} ${className}`}>
      <span className="font-bold tabular-nums">{clampFi(score)}</span>
      {showLabel && <span>{fiBandLabel(band)}</span>}
      {showDescriptor && <span className="opacity-70 font-normal">· {fiDescriptor(band)}</span>}
    </span>
  );
});

/** A small band-colored dot — for pacing strips and per-day rows. */
export const FIScaleDot = memo(function FIScaleDot({ score, className = '' }: { score: number; className?: string }) {
  return <span className={`inline-block w-2 h-2 rounded-full ${fiToneForScore(score).dot} ${className}`} aria-hidden />;
});

/**
 * The 1–9 "temperature" gauge: a cool→warm track (blue → green → amber → red)
 * with a marker at the score. Makes the scale instantly legible — the whole
 * point of FI being understandable. Stops are weighted so green holds the
 * middle and only the far right turns red (score 9).
 */
const FI_TRACK = 'linear-gradient(to right, rgb(var(--fi-chill)) 0%, rgb(var(--fi-balanced)) 45%, rgb(var(--fi-elevated)) 80%, rgb(var(--fi-packed)) 100%)';

export const FIMeter = memo(function FIMeter({ score, showZones = false, className = '' }: {
  score: number; showZones?: boolean; className?: string;
}) {
  const s = clampFi(score);
  const pct = ((s - 1) / 8) * 100;
  return (
    <div className={className}>
      <div className="relative h-2 rounded-full" style={{ background: FI_TRACK }}>
        <span
          className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full border-2 border-surface shadow ${fiToneForScore(s).solid}`}
          style={{ left: `${pct}%` }}
          aria-hidden
        />
      </div>
      {showZones && (
        <div className="mt-1 flex justify-between text-[10px] font-medium">
          <span className="text-fi-chill">Chill</span>
          <span className="text-fi-balanced">Balanced</span>
          <span className="text-fi-packed">Packed</span>
        </div>
      )}
    </div>
  );
});
